import React, { useState } from 'react';
import { isFinite, isNumber, sortBy } from 'lodash/fp';
import { Box, Card, IconButton, InputAdornment, Typography } from '@material-ui/core';
import { ArrowBack, Search } from '@material-ui/icons';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import { toast } from 'react-toastify';
import { ValidationError } from 'yup';
import {
    CurrentInventoryCountQuery,
    UnitType,
    WarehousePermission,
    refetchInventoryCountsQuery,
    useFinishInventoryCountMutation,
    useUpdateInventoryCountItemMutation
} from '../../../../generated/graphql';
import FormWrapper from '../../../../components/forms/FormWrapper';
import { AdornedSimpleInput, ProgressIndicator, SimpleSelect, Toolbar, WarehouseCardCounterField } from '../../../../ui-kit';
import { StockCardCategory } from '../../../../server-types';
import { ProtectedAction } from '../../../../ui-kit/ProtectedAction';
import { useDebounce } from '../../../../utils';
import { InventoryCountsFormikValues } from './types';
import { calculateProgress, getFilteredItems, getTotalCountedItems } from './utils';
import { currentinventoryCountValidationSchema, realCountValidation } from './validation';

interface CurrentInventoryCountViewProps {
    stockCardCategories: Pick<StockCardCategory, 'id' | 'name'>[];
    currentInventoryCount: CurrentInventoryCountQuery['currentInventoryCount'];
    onClose: () => void;
    currentWarehouseId: string;
}

const STOCK_LEVEL_UPDATE_TIMEOUT = 1500;

const REAL_COUNT_FIELD_NAME = 'realCount';
const UNIT_TYPE_FIELD_NAME = 'unitType';

const CurrentInventoryCountForm = ({
    currentInventoryCount,
    stockCardCategories,
    onClose,
    currentWarehouseId,
}: CurrentInventoryCountViewProps) => {
    const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState(false);
    const { t } = useTranslation();
    const dialogName = t('Inventory count');
    const submitButtonText: string = t('Finish inventory count');
    const [updateInventoryCountItem] = useUpdateInventoryCountItemMutation();
    const [finishInventoryCount] = useFinishInventoryCountMutation({
        refetchQueries: [refetchInventoryCountsQuery({ warehouseId: currentWarehouseId })]
    });
    let updateTimeouts: Record<number, number> = {};

    const [filteredCategoryId, setFilteredCategoryId] = useState<string | null>(null);
    const [filterName, setFilterName] = useState('');

    const debouncedFilterName = useDebounce(filterName);

    const sortedCountItems = sortBy(['stockCard.stockCardCategory.name', 'stockCard.name'], currentInventoryCount.inventoryCountItems);

    const initialValues = sortedCountItems.map(inventoryCountItem => ({
        id: inventoryCountItem.id,
        realCount: inventoryCountItem.realCount,
        unitType: inventoryCountItem.stockCard.storageUnit,
        alternativeUnit: inventoryCountItem.stockCard.conversionUnit,
        name: inventoryCountItem.stockCard.name,
        categoryName: inventoryCountItem.stockCard.stockCardCategory?.name,
    }));

    const filteredCountItems = getFilteredItems({
        items: sortedCountItems,
        stockCardName: debouncedFilterName,
        stockCardCategoryId: filteredCategoryId,
    });

    const onFormSubmit = async (values: InventoryCountsFormikValues) => {
        const countedItems = getTotalCountedItems(values);
        if (countedItems < sortedCountItems.length) {
            // TODO: Show confirmation dialog
        }

        const timeoutCounts = Object.keys(updateTimeouts).length;
        if (timeoutCounts === 0) {
            await finishInventoryCount({
                variables: {
                    id: currentInventoryCount.id,
                    warehouseId: currentWarehouseId,
                },
            });
            onClose();
            return;
        }

        setSubmitButtonDisabled(true);
        setTimeout(() => {
            finishInventoryCount({
                variables: {
                    id: currentInventoryCount.id,
                    warehouseId: currentWarehouseId,
                },
            });
            onClose();
        }, STOCK_LEVEL_UPDATE_TIMEOUT);
    };

    return (
        <Card style={{ height: '100%' }}>
            <Box style={{ height: '100%' }} display="flex" flexDirection="column" px={3} py={2}>
                <Box>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                        <ArrowBack />
                    </IconButton>
                    <Box mb={2} />
                    <Typography variant='h6' component='h1'>{dialogName}</Typography>
                    <Box display='flex'>
                        <SimpleSelect
                            name='categorySelect'
                            label={t('Choose category')}
                            showEmpty
                            options={stockCardCategories}
                            value={filteredCategoryId}
                            onChange={setFilteredCategoryId}
                        />
                        <Box mr={2} />
                        <AdornedSimpleInput
                            endAdornment={(
                                <InputAdornment position='end'>
                                    <Search />
                                </InputAdornment>
                        )}
                            name='filterName'
                            label={t('Search ingredient')}
                            value={filterName}
                            onChange={setFilterName}
                        />

                    </Box>
                </Box>
                <Box style={{ height: '100%' }}>
                    <Formik
                        initialValues={{ inventoryCountItems: initialValues }}
                        validationSchema={currentinventoryCountValidationSchema}
                        onSubmit={onFormSubmit}
                    >
                        {(formikProps: FormikProps<InventoryCountsFormikValues>) => {
                            const countedItems = getTotalCountedItems(formikProps.values);
                            const progress = calculateProgress(countedItems, formikProps.values.inventoryCountItems.length);

                            const handleFieldChange = (e: React.ChangeEvent<any>) => {
                                const { name, value } = e.target;
                                const { values } = formikProps;
                                const [, index, changedValueName] = name.split('.');

                                const oldTimeout = updateTimeouts[index as number];
                                if (oldTimeout) {
                                    clearTimeout(oldTimeout);
                                }

                                const changedInventoryCount = values.inventoryCountItems[index as number];
                                const realCount = changedValueName === REAL_COUNT_FIELD_NAME
                                ? parseFloat(value)
                                : changedInventoryCount.realCount as unknown as number;

                                // Sending the request only if user has filled real count in
                                if (isFinite(realCount)) {
                                const newTimeout = setTimeout(
                                    () => {
                                        try {
                                            const validatedRealCount = realCountValidation.validateSync(realCount);
                                            // eslint-disable-next-line no-void
                                            void updateInventoryCountItem({
                                                variables: {
                                                    data: {
                                                        realCount: validatedRealCount as number,
                                                        unitType: changedValueName === UNIT_TYPE_FIELD_NAME
                                                            ? value
                                                            : changedInventoryCount.unitType,
                                                    },
                                                    id: changedInventoryCount.id,
                                                    inventoryCountId: currentInventoryCount.id,
                                                    warehouseId: currentWarehouseId,
                                                },
                                                toastConfig: {
                                                    text: `${changedInventoryCount.name} ${t('stock level was updated')}`,
                                                }
                                            });

                                            const timeOutToClear = updateTimeouts[index as number];
                                            if (timeOutToClear) {
                                                clearTimeout(timeOutToClear);
                                            }
                                        } catch (error) {
                                            if (error instanceof ValidationError) {
                                                const { errors: [firstError] } = error;
                                                toast(t(firstError), { type: 'error' });
                                            }
                                        }
                                    },
                                    STOCK_LEVEL_UPDATE_TIMEOUT,
                                );

                                updateTimeouts = {
                                    ...updateTimeouts,
                                    [index]: newTimeout,
                                };
                            }
                            };
                        return (
                            <FormWrapper isSubmitting={formikProps.isSubmitting} isDialogView fullHeight>
                                <Virtuoso
                                    data={filteredCountItems}
                                    totalCount={filteredCountItems.length}
                                    computeItemKey={(_, item) => item.id}
                                    increaseViewportBy={900}
                                    style={{ width: '100%' }}
                                    fixedItemHeight={66}
                                    itemContent={(_, inventoryCountItem) => (
                                        <React.Fragment>
                                            <WarehouseCardCounterField<InventoryCountsFormikValues>
                                                isFilled={isNumber(inventoryCountItem.realCount)}
                                                categoryName={inventoryCountItem.categoryName ?? t('Unassigned')}
                                                handleChange={handleFieldChange}
                                                label={inventoryCountItem.name}
                                                formikProps={formikProps}
                                                inputProps={{
                                                    name: `inventoryCountItems.${inventoryCountItem.index}.${REAL_COUNT_FIELD_NAME}`,
                                                    placeholder: '',
                                                    initialValue: inventoryCountItem.realCount,
                                                }}
                                                selectProps={{
                                                    name: `inventoryCountItems.${inventoryCountItem.index}.${UNIT_TYPE_FIELD_NAME}`,
                                                    defaultValue: inventoryCountItem.unitType,
                                                }}
                                                key={`inventoryCountItems.${inventoryCountItem.index}.name`}
                                                storageUnit={inventoryCountItem.unitType}
                                                alternativeUnit={inventoryCountItem.alternativeUnit as UnitType}
                                            />
                                            <Box mb={2} />
                                        </React.Fragment>
                                    )}
                                />
                                <Toolbar minHeight="100px" isBottom disableDrawerOffset>
                                    <Box width="100%" display="flex" flexDirection="column">
                                        <ProgressIndicator
                                            bottomLeftText={`${progress} % ${t('completed')}`}
                                            bottomRightText={`${countedItems}/${formikProps.values.inventoryCountItems.length}`}
                                            progress={progress}
                                        />
                                        <Box mb={2} />
                                        <ProtectedAction
                                            disabled={isSubmitButtonDisabled || formikProps.isSubmitting}
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            permissions={{ warehouse: [WarehousePermission.CanEndInventoryCount] }}
                                        >
                                            {submitButtonText}
                                        </ProtectedAction>
                                        <Box mb={2} />
                                    </Box>
                                </Toolbar>
                            </FormWrapper>
                        );
                    }}
                    </Formik>
                </Box>
            </Box>
        </Card>
    );
};

export default CurrentInventoryCountForm;
